import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageSquare, PackagePlus, Search, RefreshCw } from 'lucide-react';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'CONFIRMED', 'DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'RETURNED', 'CANCELLED'];
const LIMIT = 20;

const shipmentDestination = (shipment: any) => {
  const latestTracking = Array.isArray(shipment.tracking) ? shipment.tracking[0] : null;
  const parts = [
    shipment.receiverAddress,
    shipment.receiverCity,
    shipment.departmentBranch?.name ? `Branch: ${shipment.departmentBranch.name}` : null,
  ].filter(Boolean);
  return {
    label: parts.join(', ') || '—',
    location: latestTracking?.location,
  };
};

export const SuperAdminShipments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status') ?? '';
  const companyParam = searchParams.get('companyId') ?? '';
  const [shipments, setShipments] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(statusParam);
  const [companyFilter, setCompanyFilter] = useState(companyParam);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.listCompanies(200, 0).then(r => {
      const d = r.data.data;
      setCompanies(d?.companies ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setStatusFilter(statusParam);
    setCompanyFilter(companyParam);
    setPage(0);
  }, [statusParam, companyParam]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelected(new Set());
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.search = search;
      if (companyFilter) filters.companyId = companyFilter;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.listShipments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setShipments(d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch (e: any) {
      setShipments([]);
      setTotal(0);
      setError(e.response?.data?.error || 'Failed to load shipments. Please refresh or check the API server.');
    } finally { setLoading(false); }
  }, [page, search, statusFilter, companyFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => setSelected(prev =>
    prev.size === shipments.length ? new Set() : new Set(shipments.map(s => s.id))
  );

  const bulkAction = async (action: 'confirm' | 'cancel') => {
    if (!selected.size) return;
    if (!globalThis.confirm(`${action === 'confirm' ? 'Confirm' : 'Cancel'} ${selected.size} shipment(s)?`)) return;
    setBulkLoading(true);
    try { await apiClient.bulkShipmentAction(Array.from(selected), action); load(); }
    catch {} finally { setBulkLoading(false); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">All Shipments <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 400 }}>({total.toLocaleString()})</span></h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={load} disabled={loading}><RefreshCw size={14} /></button>
          <Link to="/super-admin/shipments/create" className="btn btn-primary"><PackagePlus size={16} /> New Shipment</Link>
        </div>
      </div>

      <div className="card">
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search tracking, receiver, address, reference..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
          <select value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setPage(0); }} style={{ width: 180 }}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" style={{ width: 140 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" style={{ width: 140 }} />
          <button className="btn btn-ghost" onClick={() => { setSearch(''); setStatusFilter(''); setCompanyFilter(''); setDateFrom(''); setDateTo(''); setPage(0); }}>Clear</button>
        </div>

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, padding: '8px 12px', background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe' }}>
            <span style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 500 }}>{selected.size} selected</span>
            <button className="btn btn-success" style={{ padding: '3px 10px', fontSize: 12 }} disabled={bulkLoading} onClick={() => bulkAction('confirm')}>Confirm All</button>
            <button className="btn btn-danger" style={{ padding: '3px 10px', fontSize: 12 }} disabled={bulkLoading} onClick={() => bulkAction('cancel')}>Cancel All</button>
            <button className="btn btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => setSelected(new Set())}>Deselect</button>
          </div>
        )}

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {loading ? <div className="loading-spinner">Loading shipments...</div> : error ? null : shipments.length === 0 ? (
          <div className="empty-state"><h3>No shipments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th><input type="checkbox" checked={selected.size === shipments.length && shipments.length > 0} onChange={toggleAll} /></th>
                    <th>Tracking #</th><th>Sender</th><th>Receiver</th><th>Destination</th><th>Company</th><th>Status</th><th>Amount</th><th>Latest Comment</th><th>Date</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s: any) => {
                    const destination = shipmentDestination(s);
                    const latestComment = Array.isArray(s.comments) ? s.comments[0] : null;
                    return (
                      <tr key={s.id} style={{ background: selected.has(s.id) ? '#f0f9ff' : undefined }}>
                        <td><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} /></td>
                        <td>
                          <code style={{ fontSize: 11 }}>{s.trackingNumber}</code>
                          {s.references && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Ref: {s.references}</div>}
                        </td>
                        <td>{s.senderName}</td>
                        <td>
                          <div>{s.receiverName}</div>
                          {s.receiverPhone && <div style={{ fontSize: 12, color: '#64748b' }}>{s.receiverPhone}</div>}
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <div style={{ fontSize: 12, color: '#334155' }}>{destination.label}</div>
                          {destination.location && destination.location !== destination.label && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Location: {destination.location}</div>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{s.company?.name ?? '—'}</td>
                        <td><span className={`badge badge-${s.status?.toLowerCase().replaceAll('_', '-')}`}>{s.status}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}>{Number(s.totalAmount).toLocaleString()} {s.currency || 'RWF'}</td>
                        <td style={{ minWidth: 190, maxWidth: 240 }}>
                          {latestComment ? (
                            <div title={latestComment.comment}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>
                                <MessageSquare size={12} /> Internal
                              </div>
                              <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {latestComment.comment}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                        <td><Link to={`/super-admin/shipments/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
